import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  startLoadingRecordChecklist,
  startSelectAllChecklist,
  startToggleChecklistItem
} from '../../../app/actions/flowChecklistActions';
import { getChecklistLevel, isChecklistEnabledForProcess } from '../../../app/helpers/flowEngineConfigHelper';

const FlowChecklistPanel = ({
  record,
  moduleId,
  moduleProcess,
  roleId,
  canOperate = true,
  referenceIds = [],
  poolSurface = "",
  activeCatalogRoleId = null,
  activeRoleKey = ""
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const configuration = useSelector((state) => state.configuration);
  const { recordChecklist, loadingRecord } = useSelector((state) => state.flowChecklist || {});

  const enabled = isChecklistEnabledForProcess(configuration, moduleProcess);
  const level = getChecklistLevel(configuration);
  const snapshot = recordChecklist?.checklist || recordChecklist;
  const items = snapshot?.items || [];
  const targetIds = useMemo(() => (
    (referenceIds.length
      ? referenceIds
      : (record?.id ? [record.id] : [])
    ).map((id) => Number(id)).filter((id) => Number.isFinite(id))
  ), [referenceIds, record?.id]);
  const primaryId = targetIds[0] || record?.id;
  const isMass = targetIds.length > 1;
  const targetKey = targetIds.join(',');

  useEffect(() => {
    if (!enabled || !primaryId || !moduleId || !roleId) {
      return;
    }
    const query = {
      catalogrole_id: roleId,
      process: moduleProcess
    };
    if (isMass) {
      query.reference_ids = targetKey;
    }
    dispatch(startLoadingRecordChecklist(moduleId, primaryId, query));
  }, [dispatch, enabled, primaryId, moduleId, roleId, moduleProcess, isMass, targetKey]);

  const handleToggle = (item) => {
    if (!canOperate) {
      return;
    }
    dispatch(startToggleChecklistItem({
      catalogmodule_id: moduleId,
      reference_id: primaryId,
      catalogrole_id: roleId,
      def_id: item.id,
      checked: !item.checked,
      process: moduleProcess,
      pool_surface: poolSurface,
      active_catalogrole_id: activeCatalogRoleId,
      active_role_key: activeRoleKey,
      ...(isMass ? { mass_targets: targetIds } : {})
    }));
  };

  const handleSelectAll = () => {
    if (!canOperate) {
      return;
    }
    dispatch(startSelectAllChecklist({
      catalogmodule_id: moduleId,
      reference_id: primaryId,
      catalogrole_id: roleId,
      process: moduleProcess,
      pool_surface: poolSurface,
      active_catalogrole_id: activeCatalogRoleId,
      active_role_key: activeRoleKey,
      ...(isMass ? { mass_targets: targetIds } : {})
    }));
  };

  if (!enabled || !primaryId || !roleId) {
    return null;
  }

  return (
    <div className="border rounded p-3 mb-3 bg-white">
      {isMass && (
        <p className="small text-muted mb-2">
          {t(
            'flow.checklist.mass-hint',
            'Los cambios del checklist se aplican a los {{count}} registros seleccionados.',
            { count: targetIds.length }
          )}
        </p>
      )}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <strong>
          {level === 'activity'
            ? t('flow.checklist.title-activity', 'Actividades de la bandeja')
            : t('flow.checklist.title-subactivity', 'Subactividades de la bandeja')}
        </strong>
        {canOperate && items.length > 0 && (
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleSelectAll}>
            {t('flow.checklist.select-all', 'Seleccionar todas')}
          </button>
        )}
      </div>
      {loadingRecord && <p className="small text-muted mb-0">{t('general.loading', 'Cargando...')}</p>}
      {!loadingRecord && items.length === 0 && (
        <p className="small text-muted mb-0">{t('flow.checklist.empty', 'No hay actividades parametrizadas para esta bandeja.')}</p>
      )}
      <div className="d-flex flex-column gap-2">
        {items.map((item) => (
          <label key={item.id} className="d-flex align-items-start gap-2 mb-0">
            <input
              type="checkbox"
              className="mt-1"
              checked={!!item.checked}
              disabled={!canOperate}
              onChange={() => handleToggle(item)}
            />
            <span>
              {item.i18n_key
                ? t(item.i18n_key)
                : item.title}
              {item.required_to_advance && (
                <span className="badge bg-danger-subtle text-danger ms-2">
                  {t('flow.checklist.required', 'Requerida')}
                </span>
              )}
            </span>
          </label>
        ))}
      </div>
      {snapshot && snapshot.enabled && !snapshot.canAdvance && (
        <p className="small text-danger mt-2 mb-0">
          {t('flow.checklist.blocked', 'Debe completar las actividades requeridas para avanzar.')}
        </p>
      )}
    </div>
  );
};

FlowChecklistPanel.propTypes = {
  record: PropTypes.object,
  moduleId: PropTypes.number,
  moduleProcess: PropTypes.string,
  roleId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  canOperate: PropTypes.bool,
  referenceIds: PropTypes.array,
  poolSurface: PropTypes.string,
  activeCatalogRoleId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  activeRoleKey: PropTypes.string
};

export default FlowChecklistPanel;
